import { useEffect, useRef } from "react";
import {
  canShowOpusAgentCanvas,
  useOpusAgentCanvasStore,
} from "./useOpusAgentCanvasStore";
import type { OpusAgentTurnState } from "./useOpusAgentChat";

const planEventIds = (turns: readonly OpusAgentTurnState[]): string[] =>
  turns.flatMap((turn) =>
    turn.events
      .filter((event) => !!event.payload.planProposal)
      .map((event) => event.id),
  );

/**
 * Opens the canvas when a plan arrives live while the panel is open: a
 * planProposal event that was not there when the chat on screen loaded,
 * whatever its turn's status (a plan and the turn's completion can land in
 * the same poll). Plans a chat already holds when it loads (history, a chat
 * switch, the render where hydration lands) only seed the seen set. Only
 * on a page with the canvas slot (OpusAgentCanvasSlot).
 */
export const useAutoOpenPlanCanvas = ({
  chatId,
  turns,
  loading,
  panelOpen,
}: {
  chatId: string | null;
  turns: readonly OpusAgentTurnState[];
  loading: boolean;
  panelOpen: boolean;
}): void => {
  const seenRef = useRef<Set<string> | null>(null);
  const chatRef = useRef<string | null>(chatId);
  const wasLoadingRef = useRef(loading);
  useEffect(() => {
    const wasLoading = wasLoadingRef.current;
    wasLoadingRef.current = loading;
    // A switch to another chat (or a new one) starts over from what it
    // already holds, and so does the render where its history lands
    // (loading just went false). A new chat's first send gets its id here
    // too: its only turn is the one just sent, which has no plan yet.
    if (
      seenRef.current === null ||
      chatRef.current !== chatId ||
      loading ||
      wasLoading
    ) {
      chatRef.current = chatId;
      seenRef.current = new Set(planEventIds(turns));
      return;
    }
    const seen = seenRef.current;
    let arrived: string | null = null;
    for (const id of planEventIds(turns)) {
      if (seen.has(id)) continue;
      seen.add(id);
      arrived = id;
    }
    if (arrived && chatId && panelOpen && canShowOpusAgentCanvas())
      useOpusAgentCanvasStore.getState().openPlan(chatId, arrived);
  }, [chatId, turns, loading, panelOpen]);
};
